import { SafeUrl } from '@angular/platform-browser';
import { Observable, BehaviorSubject } from 'rxjs';

export enum PHX_FILE_LOADING_STATUS {
  START = 'start',
  PROGRESS = 'progress',
  DONE = 'done',
  ERROR = 'error',
}

export interface PhxFile {
  id?: string | number;
  /**
   * Client-generated correlation id used before server assigns a persistent id.
   * Mirrors webapp "offline_id" usage for caching/retries.
   */
  offline_id?: string;
  name: string;
  url: string;
  type?: string;
  size?: number;
  error?: string;
  base64?: string | ArrayBuffer | null;
  originalFile?: File;
  loaded?: number;
  phxUpdatedFile?: File;
  // Error handling additions
  errorCode?: PhxFileErrorCode; // machine-readable error category
  errorMessage?: string;        // localized human-readable message
  errorMessages?: string[];     // Error message for files (visible for tile layout)
  retryable?: boolean;          // can user retry upload/processing
  blocking?: boolean;           // blocks parent form submission until resolved
  disabled?: boolean;           // marks file as disabled/inactive
  progress$?: BehaviorSubject<number>;
  status$?: BehaviorSubject<PHX_FILE_LOADING_STATUS>;
}

export enum PhxFileErrorCode {
  NONE = 'none',
  UNSUPPORTED_FORMAT = 'unsupported_format',
  UNSUPPORTED_SIZE = 'unsupported_size',
  EMPTY_FILE = 'empty_file',
  CORRUPT_FILE = 'corrupt_file',
  LIMIT_EXCEEDED = 'limit_exceeded',
  FETCH_FAILED = 'fetch_failed',
  NETWORK = 'network',
  SERVER = 'server',
  PROCESSING = 'processing',
}

export interface PhxFileRejection {
  name: string;
  size: number;
  code: PhxFileErrorCode;
  message: string;
}

/** Broad file category. Consumers narrow the exact formats via PHX_FILE_FORMAT. */
export enum PHX_FILE_TYPE {
  IMAGES = 'images',
  VIDEOS = 'videos',
  DOCUMENTS = 'documents',
}

/** Granular, individually selectable upload formats. Values mirror the file extension. */
export enum PHX_FILE_FORMAT {
  PNG = 'png',
  JPG = 'jpg',
  JPEG = 'jpeg',
  PDF = 'pdf',
  TXT = 'txt',
  CSV = 'csv',
  GEOJSON = 'geojson',
  JSON = 'json',
  MP4 = 'mp4',
  MOV = 'mov',
  WEBM = 'webm',
}

/**
 * Single source of truth mapping each format to its accepted MIME type(s) and file extension.
 * Some formats list multiple MIME aliases because browsers/OSes report them inconsistently
 * (e.g. a `.jpg` may arrive as `image/jpeg` or `image/jpg`; a `.geojson` as `application/geo+json`
 * or `application/json`), so any listed alias is accepted for that format.
 */
export const PHX_FILE_FORMAT_MIME: Record<PHX_FILE_FORMAT, { mimes: string[]; ext: string }> = {
  [PHX_FILE_FORMAT.PNG]: { mimes: ['image/png'], ext: 'png' },
  [PHX_FILE_FORMAT.JPG]: { mimes: ['image/jpg', 'image/jpeg'], ext: 'jpg' },
  [PHX_FILE_FORMAT.JPEG]: { mimes: ['image/jpeg', 'image/jpg'], ext: 'jpeg' },
  [PHX_FILE_FORMAT.PDF]: { mimes: ['application/pdf'], ext: 'pdf' },
  [PHX_FILE_FORMAT.TXT]: { mimes: ['text/plain'], ext: 'txt' },
  [PHX_FILE_FORMAT.CSV]: { mimes: ['text/csv'], ext: 'csv' },
  [PHX_FILE_FORMAT.GEOJSON]: { mimes: ['application/geo+json', 'application/json'], ext: 'geojson' },
  [PHX_FILE_FORMAT.JSON]: { mimes: ['application/json'], ext: 'json' },
  [PHX_FILE_FORMAT.MP4]: { mimes: ['video/mp4'], ext: 'mp4' },
  [PHX_FILE_FORMAT.MOV]: { mimes: ['video/quicktime'], ext: 'mov' },
  [PHX_FILE_FORMAT.WEBM]: { mimes: ['video/webm'], ext: 'webm' },
};

/**
 * Default formats accepted for each broad type when phxAllowedFormats is not provided.
 * DOCUMENTS intentionally keeps conservative set (images + pdf); consumers that need
 * additional formats (txt, csv, geojson, json, ...) opt in explicitly via phxAllowedFormats.
 */
export const PHX_TYPE_DEFAULT_FORMATS: Record<PHX_FILE_TYPE, PHX_FILE_FORMAT[]> = {
  [PHX_FILE_TYPE.IMAGES]: [PHX_FILE_FORMAT.PNG, PHX_FILE_FORMAT.JPG, PHX_FILE_FORMAT.JPEG],
  [PHX_FILE_TYPE.VIDEOS]: [PHX_FILE_FORMAT.MP4, PHX_FILE_FORMAT.MOV, PHX_FILE_FORMAT.WEBM],
  [PHX_FILE_TYPE.DOCUMENTS]: [
    PHX_FILE_FORMAT.PNG,
    PHX_FILE_FORMAT.JPG,
    PHX_FILE_FORMAT.JPEG,
    PHX_FILE_FORMAT.PDF,
  ],
};

export const PHX_IMAGE_MIME_MAPPINGS = {
  'image/png': 'png',
  'image/jpg': 'jpg',
  'image/jpeg': 'jpeg',
} as const;

export const PHX_DOCUMENT_MIME_MAPPINGS = {
  ...PHX_IMAGE_MIME_MAPPINGS,
  'application/pdf': 'pdf',
} as const;

export const PHX_VIDEO_MIME_MAPPINGS = {
  'video/mp4': 'mp4',
  'video/quicktime': 'mov',
  'video/webm': 'webm',
} as const;

export enum PHX_LAYOUT_TYPE {
  WRAPPED = 'wrapped',
  INLINE_NO_DETAIL = 'inline-no-detail',
  TILES = 'tiles',
}

export enum PHX_FILE_ACTION {
  DELETE = 'delete',
  DOWNLOAD = 'download',
  ZOOM = 'zoom',
  ROTATE = 'rotate',
}

export type PhxLayoutType = PHX_LAYOUT_TYPE.WRAPPED | PHX_LAYOUT_TYPE.INLINE_NO_DETAIL | PHX_LAYOUT_TYPE.TILES;

export interface PhxFileUploadLink {
  id?: number;
  upload_url: string;
  doc_type: string;
  offline_id: string;
}

export type PhxUploadURLProviderFn = (file: PhxFile) => Observable<PhxFileUploadLink>;

export type PhxFileEventPayload = { file: PhxFile; index: number };

export const BYTES_IN_MB = 1024 * 1024;
export const DEFAULT_MAX_FILE_SIZE_MB = 5;
